import {
  darkModeOf,
  drawnFinishValue,
  finishSpec,
  layoutSpec,
  offeredLayoutOptions,
  paletteSpec,
  type StudioChoice,
} from '@base/shared';
import type { TFunction } from 'i18next';
import { optionKey, resolvedStudio, reviewOf } from './state.ts';

/**
 * O trecho do guia de design (DESIGN.md, ADR 0119) que sai das escolhas do estúdio (ADR 0133,
 * parte 5): a cara e o jeito de usar, em palavras, nunca em código de cor. Quem cria telas (a
 * pessoa ou a IA) lê isto; os valores exatos continuam no tema.
 */
export function designGuideOf(
  s: StudioChoice,
  t: TFunction,
  lang: string,
  appName: string,
): string {
  const localized = (x: Partial<Record<string, string>> | undefined) =>
    x?.[lang] ?? x?.['pt-BR'] ?? '';
  const { layout, finish } = resolvedStudio(s);
  const review = reviewOf(s, appName);
  const L = layoutSpec(s.layout.id);
  const F = finishSpec(s.finish.id);
  const palette =
    s.palette.id === 'logo' ? t('style.fromLogo') : localized(paletteSpec(s.palette.id)?.name);
  const fin = (k: Parameters<typeof drawnFinishValue>[0]) =>
    `${t(`style.finishOptions.${k}.label`)}: ${t(`style.finishOptions.${k}.values.${optionKey(drawnFinishValue(k, finish[k]))}`)}`;
  const look = [
    `${t('style.summaryLayout')}: ${localized(L?.name)}. ${localized(L?.description)}`,
    `${t('style.summaryPalette')}: ${palette}${s.palette.accent ? ` ${t('style.guide.ownAccent')}` : ''}${s.palette.colors ? ` ${t('style.guide.ownColors')}` : ''}.`,
    `${t('style.summaryDark')}: ${t(`style.darkMode.values.${darkModeOf(s)}`)}.`,
    `${t('style.summaryFinish')}: ${localized(F?.name)}. ${localized(F?.description)}`,
    `${fin('font')}; ${fin('radius')}; ${fin('panel')}.`,
  ];
  const use = [
    ...offeredLayoutOptions(s.layout.id).map(
      (k) =>
        `${t(`style.layoutOptions.${k}.label`)}: ${t(`style.layoutOptions.${k}.values.${layout[k]}`)}.`,
    ),
    `${t('style.summaryMobile')}: ${t(`style.mobileNavs.${s.mobileNav ?? 'drawer'}`)}.`,
  ];
  const adjust = review.changedFinish
    .filter((k) => !['font', 'radius', 'panel'].includes(k))
    .map((k) => `${fin(k)}.`);
  const list = (items: string[]) => items.map((i) => `- ${i}`).join('\n');
  return [
    `## ${t('style.guide.look')}`,
    '',
    list(look),
    '',
    `## ${t('style.guide.use')}`,
    '',
    list(use),
    ...(adjust.length ? ['', `## ${t('style.guide.details')}`, '', list(adjust)] : []),
    '',
    t('style.guide.footer'),
    '',
  ].join('\n');
}
